import { cva, type VariantProps } from 'class-variance-authority'
import { Loader2 } from 'lucide-react'
import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { cn } from '@/shared/utils/cn'

/**
 * Colour note — see docs/adr/0005-cta-colour.md and 0010-figma-fidelity-vs-wcag.md.
 *
 * The VFS guideline makes orange the primary action colour, but its #e86020 carries
 * white 16px text at 3.43:1, below the 4.5:1 that WCAG 2.1 SC 1.4.3 requires. We
 * therefore fill with `action` (Figma's own p-dark #c94c12, 4.64:1) and reserve the
 * brand orange for non-text uses where 3:1 is the bar. Hover darkens the fill under
 * white text, so every state passes by construction rather than by separate checking.
 *
 * The foreground is `on-action`, not `text-white`: Figma specifies the label as
 * #fafafa, which measures 4.45:1 on the fill and fails. Routing it through the token
 * keeps that decision in the theme's swap block rather than buried here.
 *
 * DISABLED is variant-scoped, not shared. The redesign gives the primary button a
 * solid slate fill under a white label; putting that in the base would land it
 * behind `tertiary` (an underlined text link) and `danger`.
 * Leaving `opacity-50` in the base alongside it would be worse still: 50%-opacity
 * slate reads as a wrong token value rather than as a leftover rule. So the fill
 * lives on `primary` and the opacity is pushed down into the other three.
 */
const button = cva(
  [
    'inline-flex items-center justify-center gap-1 rounded font-medium',
    'transition-colors duration-150',
    'disabled:cursor-not-allowed',
    // SC 2.5.8: a 44px target is the minimum for a citizen tapping on a phone.
    // --spacing is rebased to 8px in theme.css, so this is 5.5, not 11.
    'min-w-5.5',
  ],
  {
    variants: {
      variant: {
        primary: [
          'bg-action text-on-action hover:bg-action-hover',
          'disabled:bg-disabled disabled:text-on-disabled disabled:hover:bg-disabled',
        ],
        secondary: [
          'border border-panel bg-transparent text-text',
          'hover:bg-panel hover:text-white disabled:hover:bg-transparent',
          'disabled:opacity-50 disabled:hover:text-text',
        ],
        tertiary: [
          'bg-transparent text-action-text underline hover:text-action-text-hover',
          'disabled:opacity-50',
        ],
        danger: 'bg-error-600 text-white hover:brightness-90 disabled:opacity-50',
      },
      size: {
        // `lg` is the Figma primary: 52px tall, its own 10px radius (one step off
        // the 12px field it sits under) and a 16/24 semibold label.
        //
        // `font-semibold` is not redundant with `text-title-s`, which carries 600 as
        // its paired weight: the base above sets `font-medium`, and a font-size
        // utility's paired weight loses to an explicit one. Without this the label
        // renders at 500 and quietly contradicts the line above it.
        lg: 'h-btn-lg gap-0.75 rounded-btn px-4 text-title-s font-semibold',
        md: 'h-btn-md px-3 text-label',
        sm: 'h-btn-sm px-3 text-micro',
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md', fullWidth: false },
  }
)

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof button> {
  /** Renders a spinner and blocks interaction without changing the button's width. */
  loading?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, fullWidth, loading = false, disabled, children, ...props },
  ref
) {
  return (
    <button
      ref={ref}
      type={props.type ?? 'button'}
      // aria-busy rather than swapping the label: a screen reader user who has just
      // pressed submit should not have the accessible name change underneath them.
      aria-busy={loading || undefined}
      disabled={disabled ?? loading}
      className={cn(button({ variant, size, fullWidth }), className)}
      {...props}
    >
      {loading && <Loader2 aria-hidden="true" className="size-2 animate-spin" />}
      {children}
    </button>
  )
})
